import {
    Box,
    Button, Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem, Select, Switch, TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { updateLocation } from '../../action/locationsCommands';
import { Location, NotificationType } from '../../api/types';
const PREFIX = 'EditLocationDialog';

const classes = {
    idMatchingContainer: `${PREFIX}-idMatchingContainer`,
    notificationTypeSuccess: `${PREFIX}-notificationTypeSuccess`,
    notificationTypeWarning: `${PREFIX}-notificationTypeWarning`,
    notificationTypeError: `${PREFIX}-notificationTypeError`,
    notificationTypeInfo: `${PREFIX}-notificationTypeInfo`,
    showNotificationSwitch: `${PREFIX}-showNotificationSwitch`,
    notificationText: `${PREFIX}-notificationText`,
    accessoriesContainer: `${PREFIX}-accessoriesContainer`,
    accessoriesHeader: `${PREFIX}-accessoriesHeader`,
};

const StyledDialog = styled(Dialog)((
    {
        theme,
    },
) => ({
    [`& .${classes.idMatchingContainer}`]: {
        margin: `${theme.spacing(2)} 0`,
        display: 'flex',
        '& > *': {
            flex: 1,
            '&:not(:last-child)': {
                marginRight: theme.spacing(1),
                [theme.breakpoints.down('sm')]: {
                    marginRight: 0,
                    marginBottom: theme.spacing(2),
                },
            },
        },
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },

    [`& .${classes.notificationTypeSuccess}`]: {
        color: theme.palette.success.main,
        fontWeight: 500,
    },

    [`& .${classes.notificationTypeWarning}`]: {
        color: theme.palette.warning.main,
        fontWeight: 500,
    },

    [`& .${classes.notificationTypeError}`]: {
        color: theme.palette.error.main,
        fontWeight: 500,
    },

    [`& .${classes.notificationTypeInfo}`]: {
        color: theme.palette.info.main,
        fontWeight: 500,
    },

    [`& .${classes.showNotificationSwitch}`]: {
        marginBottom: theme.spacing(1),
    },

    [`& .${classes.notificationText}`]: {
        marginTop: theme.spacing(1),
    },

    [`& .${classes.accessoriesContainer}`]: {
        marginTop: theme.spacing(3),
    },

    [`& .${classes.accessoriesHeader}`]: {
        fontWeight: 'bold',
    },
}));

interface EditLocationDialogProps {
    open: boolean;
    onClose: () => void;
    location: Location;
}

const EditLocationDialog = (props: EditLocationDialogProps) => {

    const dispatch = useDispatch();
    const {t} = useTranslation();
    const [enabled, setEnabled] = useState<boolean>(props.location.enabled);
    const [bookable, setBookable] = useState<boolean>(props.location.status.bookable);
    const [showNotification, setShowNotification] = useState<boolean>(Boolean(props.location.status.notification));
    const [notificationType, setNotificationType] = useState<NotificationType>(
        props.location.status.notification?.type || 'info',
    );
    const [notificationText, setNotificationText] = useState<string>(
        props.location.status.notification?.text || '',
    );
    const resetDialog = () => {
        setEnabled(props.location.enabled);
        setBookable(props.location.status.bookable);
        setShowNotification(Boolean(props.location.status.notification));
        setNotificationType(props.location.status.notification?.type || 'info');
        setNotificationText(props.location.status.notification?.text || '');
    };

    useEffect(resetDialog, []);
    useEffect(resetDialog, [props.location, props.open]);

    const handleSaveClick = () => {

        dispatch(updateLocation({
            id: props.location.id,
            enabled,
            status: {
                bookable,
                ...(!showNotification ? {} : {
                    notification: {
                        type: notificationType,
                        text: notificationText,
                    },
                }),
            },
        }));

        props.onClose();
    };

    const handleNotificationTypeChange = (event: React.ChangeEvent<{ value: NotificationType }>) => {
        setNotificationType(event.target.value);
    };

    const handleNotificationTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotificationText(event.target.value);
    };

    return (
        <StyledDialog open={props.open} onClose={props.onClose} fullWidth={true}>
            <DialogTitle>{props.location.name}</DialogTitle>
            <DialogContent>
                <Box>
                    <FormControlLabel
                        control={(
                            <Switch checked={enabled} onChange={() => setEnabled(!enabled)} color={'secondary'} />
                        )}
                        label={t('common.active')}
                    />
                    <FormControlLabel
                        control={(
                            <Switch checked={bookable} onChange={() => setBookable(!bookable)} color={'secondary'} />
                        )}
                        label={t('common.bookable')}
                    />
                </Box>
                <Collapse in={showNotification} collapsedSize={30}>
                    <Box>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={showNotification}
                                    onChange={() => setShowNotification(!showNotification)}
                                />
                            )}
                            label={t('location_details.show_notification')}
                            className={classes.showNotificationSwitch}
                        />
                        <Select
                            variant={'outlined'}
                            fullWidth={true}
                            value={notificationType}
                            onChange={handleNotificationTypeChange as any}
                            className={notificationType === 'success'
                                ? classes.notificationTypeSuccess
                                : notificationType === 'warning'
                                    ? classes.notificationTypeWarning
                                    : notificationType === 'error'
                                        ? classes.notificationTypeError
                                        : classes.notificationTypeInfo
                            }
                        >
                            <MenuItem value={'success'} className={classes.notificationTypeSuccess}>
                                {t('location_details.notification_type.success')}
                            </MenuItem>
                            <MenuItem value={'warning'} className={classes.notificationTypeWarning}>
                                {t('location_details.notification_type.warning')}
                            </MenuItem>
                            <MenuItem value={'error'} className={classes.notificationTypeError}>
                                {t('location_details.notification_type.error')}
                            </MenuItem>
                            <MenuItem value={'info'} className={classes.notificationTypeInfo}>
                                {t('location_details.notification_type.info')}
                            </MenuItem>
                        </Select>
                        <TextField
                            variant={'outlined'}
                            fullWidth={true}
                            multiline={true}
                            rows={4}
                            className={classes.notificationText}
                            value={notificationText}
                            onChange={handleNotificationTextChange}
                        />
                    </Box>
                </Collapse>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>{t('common.cancel')}</Button>
                <Button variant={'contained'} color={'primary'} onClick={handleSaveClick}>{t('common.save')}</Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default EditLocationDialog;
